import { useClerk, useSession } from "@clerk/nextjs";
import { datadogRum } from "@datadog/browser-rum";
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
import {
  AllEnterpriseModule,
  ClientSideRowModelModule,
  IntegratedChartsModule,
  LicenseManager,
  ModuleRegistry,
  PivotModule,
  RowGroupingPanelModule,
} from "ag-grid-enterprise";
import { NextPage } from "next";
import { type AppProps } from "next/app";
import { useRouter } from "next/router";
import { ReactElement, ReactNode, useEffect } from "react";

import { PageHead } from "~/components/_shared/PageHead";
import { useSavedFilterRouteLifecycle } from "~/components/filters/useSavedFilter";
import { AnnouncementBanner } from "~/components/layout/AnnouncementBanner";
import { ShelfCycleErrorBoundary } from "~/components/ShelfCycleErrorBoundary";
import { AppProvider } from "~/contexts/appProvider";
import { checkDomain } from "~/domains";
import { env } from "~/env";
import { useClerkAuthRecoverySessionRegistration } from "~/hooks/useClerkAuthRecoverySessionRegistration";
import { useIdentityScopedQueryCache } from "~/hooks/useIdentityScopedQueryCache";
import { CLERK_AUTH_RECOVERY_ACTION, configureClerkAuthRecoveryCoordinator } from "~/services/clerk-auth-recovery.service";
import { CLERK_REFRESH_ANOMALY_ACTION, createClerkRefreshDiagnosticsService } from "~/services/clerk-refresh-diagnostics.service";
import "~/styles/globals.css";
import "~/styles/print.css";

import AppContainer from "~/components/AppContainer";
import { api } from "~/utils/api";

// Register AG Grid Enterprise modules
ModuleRegistry.registerModules([
  AllEnterpriseModule,
  IntegratedChartsModule.with(AgChartsEnterpriseModule),
  ClientSideRowModelModule,
  PivotModule,
  RowGroupingPanelModule,
]);

// Set AG Grid Enterprise license key
const licenseKey: string = env.NEXT_PUBLIC_AG_GRID_LICENSE_KEY || "";

LicenseManager.setLicenseKey(licenseKey);

const clerkRefreshDiagnostics = createClerkRefreshDiagnosticsService(undefined, action =>
  datadogRum.addAction(CLERK_REFRESH_ANOMALY_ACTION, action),
);

if (typeof window !== "undefined" && checkDomain(window.location.host)) {
  datadogRum.init({
    applicationId: "14e9b6e8-a684-4797-b076-32144fd3179f",
    clientToken: "pub648b8f1566e024deaca566a8f6ad0611",
    site: "us5.datadoghq.com",
    service: "shelfcycle",
    version: env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
    sessionSampleRate: 100,
    premiumSampleRate: 100,
    trackUserInteractions: true,
    defaultPrivacyLevel: "allow",
    beforeSend: (event, context) => {
      clerkRefreshDiagnostics.observeRumEvent(event, context);
    },
  });
}
const SessionRecorder = () => {
  const clerk = useClerk();
  const { isLoaded: isSessionLoaded, session } = useSession();
  const email = clerk.user?.primaryEmailAddress?.emailAddress;
  const org = clerk.organization?.name;
  const isSignedIn = isSessionLoaded && session != null;
  const isAppDomain = typeof window !== "undefined" && checkDomain(window.location.host);
  useClerkAuthRecoverySessionRegistration(isAppDomain);
  useEffect(() => {
    if (!isAppDomain) {
      return;
    }
    configureClerkAuthRecoveryCoordinator(action => datadogRum.addAction(CLERK_AUTH_RECOVERY_ACTION, action));
  }, [isAppDomain]);
  useEffect(() => {
    if (typeof window !== "undefined" && checkDomain(window.location.host)) {
      datadogRum.startSessionReplayRecording();
      datadogRum.setUser({ email, org });
    }
  }, [email, org]);
  useEffect(() => {
    if (typeof window === "undefined" || !checkDomain(window.location.host) || !isSignedIn) {
      return;
    }

    return clerkRefreshDiagnostics.start(() => {
      if (typeof window === "undefined") {
        return false;
      }
      return checkDomain(window.location.host) && isSignedIn;
    });
  }, [isSignedIn]);
  return <></>;
};

/* Sits under both ClerkProvider and the tRPC query client, on every route, so a change of signed-in
   user drops query data cached for the previous one. */
const QueryCacheIdentityBoundary = () => {
  useIdentityScopedQueryCache();
  return <></>;
};

const omitAppContainerPrefixes = ["/sign-in", "/sign-up", "/switch-org", "/maintenance", "/verify-alternate-email"];

export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const App = (props: AppPropsWithLayout) => {
  const router = useRouter();
  const { pathname } = router;
  useSavedFilterRouteLifecycle(router);
  const useBareAppShell = omitAppContainerPrefixes.some(prefix => pathname.startsWith(prefix));

  return (
    <>
      <PageHead>
        <>
          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
          <link rel="apple-touch-icon" sizes="180x180" href="/favicon/apple-touch-icon.png" />
          <link rel="icon" type="image/png" sizes="32x32" href="/favicon/favicon-32x32.png" />
          <link rel="icon" type="image/png" sizes="16x16" href="/favicon/favicon-16x16.png" />
          <link rel="manifest" href="/favicon/site.webmanifest" />
          <link rel="mask-icon" href="/favicon/safari-pinned-tab.svg" color="#5198f9" />
          <meta name="msapplication-TileColor" content="#5198f9" />
          <meta name="theme-color" content="#5198F9" />
        </>
      </PageHead>
      <AppProvider pageProps={props.pageProps}>
        <SessionRecorder />
        <QueryCacheIdentityBoundary />
        {!useBareAppShell && <AnnouncementBanner />}
        {useBareAppShell ? (
          <ShelfCycleErrorBoundary key={pathname}>
            <AppLayoutEngine {...props} />
          </ShelfCycleErrorBoundary>
        ) : (
          <AppContainer>
            <ShelfCycleErrorBoundary key={pathname}>
              <AppLayoutEngine {...props} />
            </ShelfCycleErrorBoundary>
          </AppContainer>
        )}
      </AppProvider>
    </>
  );
};

const AppLayoutEngine = ({ Component, pageProps }: AppPropsWithLayout) => {
  const getLayout = Component.getLayout ?? (page => page);
  return getLayout(<Component {...pageProps} />);
};

export default api.withTRPC(App);
